import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'

interface CardProps {
  children: ReactNode
  className?: string
  variant?: 'default' | 'warning' | 'success' | 'error'
  // Backward compatibility props
  title?: string
  subtitle?: string
  footer?: ReactNode
  noPadding?: boolean
}

const variantStyles = {
  default: '',
  warning: 'bg-warning/10 border-l-4 border-l-warning',
  success: 'bg-success/10 border-l-4 border-l-success',
  error: 'bg-danger/10 border-l-4 border-l-danger',
}

export function Card({ children, className, variant = 'default', title, subtitle, footer, noPadding = false }: CardProps) {
  // If using old API with title/subtitle/footer, render that format
  if (title || subtitle || footer) {
    return (
      <div className={cn(
        'bg-background rounded-md shadow-sm border border-border',
        variantStyles[variant],
        className
      )}>
        {/* Header */}
        {(title || subtitle) && (
          <div className="px-6 py-4 border-b border-border">
            {title && (
              <h2 className="text-xl font-bold text-text">{title}</h2>
            )}
            {subtitle && (
              <p className="text-sm text-text-muted mt-1">{subtitle}</p>
            )}
          </div>
        )}

        {/* Content */}
        <div className={cn(!noPadding && 'px-6 py-4')}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 bg-surface/50 border-t border-border rounded-b-md">
            {footer}
          </div>
        )}
      </div>
    )
  }

  // New API - just render children
  return (
    <div className={cn(
      'bg-background rounded-md shadow-sm border border-border',
      variantStyles[variant],
      className
    )}>
      {children}
    </div>
  )
}

interface CardHeaderProps {
  children: ReactNode
  className?: string
  action?: ReactNode
}

export function CardHeader({ children, className, action }: CardHeaderProps) {
  return (
    <div className={cn('px-4 py-3 border-b border-border flex items-center justify-between', className)}>
      <div>{children}</div>
      {action && <div>{action}</div>}
    </div>
  )
}

interface CardTitleProps {
  children: ReactNode
  className?: string
}

export function CardTitle({ children, className }: CardTitleProps) {
  return (
    <h3 className={cn('text-lg font-semibold text-text', className)}>
      {children}
    </h3>
  )
}

interface CardDescriptionProps {
  children: ReactNode
  className?: string
}

export function CardDescription({ children, className }: CardDescriptionProps) {
  return (
    <p className={cn('text-sm text-text-muted mt-1', className)}>
      {children}
    </p>
  )
}

interface CardBodyProps {
  children: ReactNode
  className?: string
}

export function CardBody({ children, className }: CardBodyProps) {
  return (
    <div className={cn('px-4 py-4', className)}>
      {children}
    </div>
  )
}

interface CardFooterProps {
  children: ReactNode
  className?: string
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div className={cn('px-4 py-3 border-t border-border bg-surface/50', className)}>
      {children}
    </div>
  )
}

export default Card
